/**
 * MODMANIA — Full-screen overlay menu
 * 3-level panel navigation: Root → Brands → Models
 */
(function () {
  'use strict';

  // ── Build overlay ───────────────────────────────────────────────────────

  function buildOverlay() {
    const drawer = document.querySelector('#menu-drawer');
    if (!drawer) return null;

    const navItems = parseNavItems(drawer);

    const overlay = document.createElement('div');
    overlay.id = 'mm-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Navigation menu');
    overlay.innerHTML = `
      <div id="mm-ol-header">
        <div id="mm-ol-logo-wrap">
          <button id="mm-ol-back" aria-label="Go back" hidden>‹</button>
          <span id="mm-ol-logo">MODMANIA</span>
        </div>
        <button id="mm-ol-close" aria-label="Close menu">✕</button>
      </div>
      <div id="mm-ol-divider"></div>
      <div id="mm-ol-crumb" hidden></div>
      <div id="mm-ol-panels"></div>
      <div id="mm-ol-footer"></div>
    `;

    populateFooter(overlay.querySelector('#mm-ol-footer'), drawer);

    const panelsEl = overlay.querySelector('#mm-ol-panels');
    panelsEl.appendChild(buildRootPanel(navItems));

    navItems.forEach((item) => {
      if (item.children.length > 0) {
        panelsEl.appendChild(buildBrandsPanel(item));
        item.children.forEach((brand) => {
          panelsEl.appendChild(buildModelsPanel(item, brand));
        });
      }
    });

    return overlay;
  }

  function parseNavItems(drawer) {
    const navUl = drawer.querySelector('.menu-drawer__navigation > ul')
      || drawer.querySelector('.menu-drawer__menu.list-menu');
    if (!navUl) return [];

    const items = [];
    let index = 1;

    Array.from(navUl.children).forEach((li) => {
      if (li.tagName !== 'LI') return;

      const details = li.querySelector(':scope > details');
      const summary = details?.querySelector(':scope > summary');
      const link = li.querySelector(':scope > a');
      const label = (summary || link)?.textContent.trim();
      if (!label) return;

      const href = link?.getAttribute('href') || null;
      const children = [];

      if (details) {
        const innerSubmenu = details.querySelector('.menu-drawer__submenu');
        const brandDetailsList = innerSubmenu
          ? innerSubmenu.querySelectorAll(':scope details, .menu-drawer__inner-submenu details')
          : details.querySelectorAll('.menu-drawer__submenu details');

        brandDetailsList.forEach((brandDetails) => {
          const brandSummary = brandDetails.querySelector(':scope > summary');
          const brandName = brandSummary?.textContent.trim();
          if (!brandName) return;
          const models = Array.from(brandDetails.querySelectorAll('a')).map((a) => ({
            label: a.textContent.trim(),
            href: a.getAttribute('href'),
          }));
          children.push({ label: brandName, models });
        });
      }

      items.push({ label, href, children, index: index++ });
    });

    return items;
  }

  // ── Panel builders ──────────────────────────────────────────────────────

  function buildRootPanel(navItems) {
    const panel = document.createElement('div');
    panel.className = 'mm-panel mm-panel--root mm-panel--active';
    panel.id = 'mm-panel-root';

    const blue = document.createElement('div');
    blue.className = 'mm-blue-bar';
    panel.appendChild(blue);

    const ul = document.createElement('ul');
    ul.className = 'mm-nav';

    navItems.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'mm-nav__item';

      const currentPath = window.location.pathname;
      const isActive = item.href && (
        item.href === currentPath ||
        (item.href !== '/' && currentPath.startsWith(item.href))
      );

      if (item.children.length > 0) {
        const btn = document.createElement('button');
        btn.className = `mm-nav__btn${isActive ? ' mm-nav__btn--active' : ''}`;
        btn.dataset.panel = `mm-panel-${slugify(item.label)}`;
        btn.setAttribute('aria-expanded', 'false');

        const numSpan = document.createElement('span');
        numSpan.className = 'mm-nav__num';
        numSpan.textContent = pad(item.index);

        const labelSpan = document.createElement('span');
        labelSpan.className = 'mm-nav__label';
        labelSpan.textContent = item.label;

        const arrowSpan = document.createElement('span');
        arrowSpan.className = 'mm-nav__arrow';
        arrowSpan.textContent = '›';

        btn.append(numSpan, labelSpan, arrowSpan);
        li.appendChild(btn);
      } else {
        const a = document.createElement('a');
        a.className = `mm-nav__link${isActive ? ' mm-nav__link--active' : ''}`;
        a.href = item.href && /^\//.test(item.href) ? item.href : '#';

        const numSpan = document.createElement('span');
        numSpan.className = 'mm-nav__num';
        numSpan.textContent = pad(item.index);

        const labelSpan = document.createElement('span');
        labelSpan.className = 'mm-nav__label';
        labelSpan.textContent = item.label;

        a.append(numSpan, labelSpan);
        li.appendChild(a);
      }

      ul.appendChild(li);
    });

    panel.appendChild(ul);
    return panel;
  }

  function buildBrandsPanel(item) {
    const panel = document.createElement('div');
    panel.className = 'mm-panel mm-panel--sub';
    panel.id = `mm-panel-${slugify(item.label)}`;
    panel.dataset.crumbPath = item.label.toUpperCase();

    const blue = document.createElement('div');
    blue.className = 'mm-blue-bar';
    panel.appendChild(blue);

    const ul = document.createElement('ul');
    ul.className = 'mm-nav';

    item.children.forEach((brand, idx) => {
      const li = document.createElement('li');
      li.className = 'mm-nav__item';

      const btn = document.createElement('button');
      btn.className = 'mm-nav__btn';
      btn.dataset.panel = `mm-panel-${slugify(item.label)}-${slugify(brand.label)}`;
      btn.setAttribute('aria-expanded', 'false');

      const numSpan = document.createElement('span');
      numSpan.className = 'mm-nav__num';
      numSpan.textContent = pad(idx + 1);

      const labelSpan = document.createElement('span');
      labelSpan.className = 'mm-nav__label';
      labelSpan.textContent = brand.label;

      const arrowSpan = document.createElement('span');
      arrowSpan.className = 'mm-nav__arrow';
      arrowSpan.textContent = '›';

      btn.append(numSpan, labelSpan, arrowSpan);
      li.appendChild(btn);
      ul.appendChild(li);
    });

    panel.appendChild(ul);
    return panel;
  }

  function buildModelsPanel(parentItem, brand) {
    const panel = document.createElement('div');
    panel.className = 'mm-panel mm-panel--sub';
    panel.id = `mm-panel-${slugify(parentItem.label)}-${slugify(brand.label)}`;
    panel.dataset.crumbPath = parentItem.label.toUpperCase() + ' / ' + brand.label.toUpperCase();

    const blue = document.createElement('div');
    blue.className = 'mm-blue-bar';
    panel.appendChild(blue);

    if (brand.models.length > 0) {
      const ul = document.createElement('ul');
      ul.className = 'mm-sub-models';

      let linkCount = 0;

      brand.models.forEach((model) => {
        const li = document.createElement('li');

        if (!model.href || model.href === '#') {
          li.className = 'mm-sub-group-header';
          const span = document.createElement('span');
          span.textContent = model.label;
          li.appendChild(span);
        } else {
          linkCount++;
          const numSpan = document.createElement('span');
          numSpan.className = 'mm-sub-model-num';
          numSpan.innerHTML = '<svg width="18" height="10" viewBox="0 0 18 10" fill="none" aria-hidden="true"><line x1="0" y1="5" x2="13" y2="5" stroke="#007AFF" stroke-width="1.5" stroke-linecap="round"/><polyline points="9,1 14,5 9,9" stroke="#007AFF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

          const a = document.createElement('a');
          a.className = 'mm-sub-model-link';
          a.href = /^\//.test(model.href) ? model.href : '#';
          a.textContent = model.label.replace(/^[-–—]\s*/, '');

          li.append(numSpan, a);
        }

        ul.appendChild(li);
      });

      panel.appendChild(ul);
    }

    return panel;
  }

  function populateFooter(footerEl, drawer) {
    const accountEl = document.querySelector('.header__icon--account');
    const rawHref = accountEl?.getAttribute('href') || '/account';
    const accountA = document.createElement('a');
    accountA.className = 'mm-footer-util';
    accountA.href = /^\//.test(rawHref) ? rawHref : '/account';
    accountA.textContent = accountEl?.textContent.trim() || 'Log in';
    footerEl.appendChild(accountA);

    const socialsDiv = document.createElement('div');
    socialsDiv.className = 'mm-footer-socials';
    drawer.querySelectorAll('.list-social a').forEach((a) => {
      const link = document.createElement('a');
      link.className = 'mm-footer-social';
      const href = a.getAttribute('href') || '#';
      link.href = /^(https?:\/\/|\/\/)/.test(href) ? href : '#';
      link.setAttribute('aria-label', a.getAttribute('aria-label') || '');
      link.target = '_blank';
      link.rel = 'noopener';
      const svg = a.querySelector('svg');
      if (svg) link.appendChild(svg.cloneNode(true));
      socialsDiv.appendChild(link);
    });
    footerEl.appendChild(socialsDiv);
  }

  // ── Panel navigation ────────────────────────────────────────────────────

  let currentPanel = null;
  let overlay = null;
  let scrim = null;
  let openerElement = null;
  let panelStack = [];

  function openOverlay() {
    openerElement = document.activeElement;
    panelStack = [];

    if (!overlay) {
      overlay = buildOverlay();
      if (!overlay) return;
      document.body.appendChild(overlay);

      scrim = document.createElement('div');
      scrim.id = 'mm-scrim';
      document.body.appendChild(scrim);
      scrim.addEventListener('click', closeOverlay);

      bindOverlayEvents();
    }

    document.body.classList.add('no-scroll');
    // Reset all panels to their off-screen starting positions before showing root
    if (overlay) {
      overlay.querySelectorAll('.mm-panel').forEach((p) => p.classList.remove('mm-panel--active', 'mm-panel--exit'));
    }
    currentPanel = null;
    showPanel('mm-panel-root', 'forward');

    requestAnimationFrame(() => {
      overlay.classList.add('mm-is-open');
      scrim.classList.add('mm-is-open');
    });

    setTimeout(() => overlay.querySelector('#mm-ol-close')?.focus(), 560);
  }

  function closeOverlay() {
    overlay.classList.remove('mm-is-open');
    scrim.classList.remove('mm-is-open');
    document.body.classList.remove('no-scroll');
    panelStack = [];
    if (openerElement) {
      openerElement.focus();
      openerElement = null;
    }
  }

  function showPanel(panelId, direction) {
    const prevPanel = currentPanel;

    if (direction === 'forward' && currentPanel) {
      panelStack.push(currentPanel.id);
    }

    const target = overlay.querySelector(`#${panelId}`);
    if (!target) return;

    // Remove active from all panels but preserve existing exit states
    overlay.querySelectorAll('.mm-panel').forEach((p) => p.classList.remove('mm-panel--active'));

    if (direction === 'forward' && prevPanel) {
      // Push previous panel to the left
      prevPanel.classList.add('mm-panel--exit');
    }

    // Bring target in from its current position
    target.classList.remove('mm-panel--exit');
    target.classList.add('mm-panel--active');
    currentPanel = target;

    const isRoot = panelId === 'mm-panel-root';
    const backBtn = overlay.querySelector('#mm-ol-back');
    const crumb = overlay.querySelector('#mm-ol-crumb');

    backBtn.hidden = isRoot;
    crumb.hidden = isRoot;

    if (!isRoot) {
      crumb.textContent = '';
      crumb.appendChild(document.createTextNode('HOME / '));
      const span = document.createElement('span');
      span.textContent = target.dataset.crumbPath || '';
      crumb.appendChild(span);
    }

    overlay.querySelectorAll('.mm-nav__btn[data-panel]').forEach((btn) => {
      btn.setAttribute('aria-expanded', btn.dataset.panel === panelId ? 'true' : 'false');
    });
  }

  function goBack() {
    const prev = panelStack.pop() || 'mm-panel-root';
    showPanel(prev, 'back');
  }

  function bindOverlayEvents() {
    overlay.querySelector('#mm-ol-close').addEventListener('click', closeOverlay);
    overlay.querySelector('#mm-ol-back').addEventListener('click', goBack);

    overlay.addEventListener('click', (e) => {
      const btn = e.target.closest('.mm-nav__btn');
      if (btn) {
        const targetId = btn.dataset.panel;
        if (targetId) showPanel(targetId, 'forward');
      }
    });

    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeOverlay();
    });

    overlay.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(overlay.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex="0"]'
      )).filter((el) => !el.closest('[hidden]') && getComputedStyle(el).display !== 'none');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    });
  }

  // ── Intercept hamburger ─────────────────────────────────────────────────

  function init() {
    const header = document.querySelector('.header');
    if (header) {
      const btn = document.createElement('button');
      btn.className = 'mm-menu-trigger';
      btn.setAttribute('aria-label', 'Open menu');
      btn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="11" viewBox="0 0 18 11" fill="currentColor">
          <rect y="0" width="18" height="1.5" rx="0.75"/>
          <rect y="4.75" width="18" height="1.5" rx="0.75"/>
          <rect y="9.5" width="18" height="1.5" rx="0.75"/>
        </svg>
        <span>MENU</span>`;
      btn.addEventListener('click', openOverlay);
      header.insertBefore(btn, header.firstChild);
    }

    const hamburger = document.querySelector('.header__icon--menu');
    if (hamburger) {
      hamburger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        openOverlay();
      }, true);
    }
  }

  // ── Utilities ───────────────────────────────────────────────────────────

  function slugify(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  // ── Boot ────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
